import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { heroContent, technologySection, experienceSection, servicesPage } from '../../data/mock';
import { TrendingUp, Users } from 'lucide-react';
import Footer from '../../components/layout/Footer';

const Home = () => {
  const { language } = useLanguage();
  const hero = heroContent[language];
  const tech = technologySection[language];
  const exp = experienceSection[language];
  const services = servicesPage[language];
  
  const [currentSection, setCurrentSection] = useState(0);
  const containerRef = useRef(null);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (isScrolling.current) return;
    isScrolling.current = true;

    if (e.deltaY > 0 && currentSection < 2) {
      setCurrentSection(prev => Math.min(prev + 1, 2));
    } else if (e.deltaY < 0 && currentSection > 0) {
      setCurrentSection(prev => Math.max(prev - 1, 0));
    }

    setTimeout(() => { isScrolling.current = false; }, 800);
  }, [currentSection]);

  useEffect(() => {
    const handleWheel = (e) => {
      e.preventDefault();
      if (e.deltaY > 0 && currentSection < 2) {
        setCurrentSection(prev => Math.min(prev + 1, 2));
      } else if (e.deltaY < 0 && currentSection > 0) {
        setCurrentSection(prev => Math.max(prev - 1, 0));
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [handleWheel]);

  // Touch support for mobile swiping
  const touchStart = useRef(0);
  const handleTouchStart = useCallback((e) => {
    touchStart.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (isScrolling.current) return;
    const diff = touchStart.current - e.changedTouches[0].clientY;
    if (Math.abs(diff) < 50) return;
    isScrolling.current = true;

    if (diff > 0 && currentSection < 2) {
      setCurrentSection(prev => Math.min(prev + 1, 2));
    } else if (diff < 0 && currentSection > 0) {
      setCurrentSection(prev => Math.max(prev - 1, 0));
    }

    setTimeout(() => { isScrolling.current = false; }, 800);
  }, [currentSection]);

  const scrollToSection = (index) => {
    setCurrentSection(index);
  };

  return (
    <div
      ref={containerRef}
      className="h-screen overflow-hidden relative"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Section indicators */}
      <div className="fixed right-3 sm:right-8 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-3 animate-float">
        {[0, 1, 2].map((index) => (
          <button
            key={index}
            onClick={() => scrollToSection(index)}
            className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
              currentSection === index
                ? 'bg-[#3eb8b3] scale-125'
                : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Go to section ${index + 1}`}
          />
        ))}
      </div>

      {/* Sections container */}
      <div
        className="transition-transform duration-700 ease-in-out"
        style={{ transform: `translateY(-${currentSection * 100}vh)` }}
      >
        {/* Section 1: Hero */}
        <section className="h-screen relative">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80')`,
            }}
          >
            <div className="absolute inset-0 bg-[#1a2a3a]/70" />
          </div>

          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 sm:px-6">
            <p
              className="text-[#3eb8b3] text-sm sm:text-lg mb-3 sm:mb-4 tracking-widest uppercase"
              style={{
                opacity: currentSection === 0 ? 1 : 0,
                transform: currentSection === 0 ? 'translateY(0)' : 'translateY(24px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
                transitionDelay: '0ms',
              }}
            >
              - {hero.title} -
            </p>
            <h1
              className="text-white text-3xl sm:text-4xl md:text-6xl font-light mb-4 sm:mb-6 max-w-4xl"
              style={{
                opacity: currentSection === 0 ? 1 : 0,
                transform: currentSection === 0 ? 'translateY(0)' : 'translateY(24px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
                transitionDelay: '200ms',
              }}
            >
              {hero.subtitle}
            </h1>
            <p
              className="text-white/80 text-base sm:text-xl md:text-2xl font-light mb-6 sm:mb-8"
              style={{
                opacity: currentSection === 0 ? 1 : 0,
                transform: currentSection === 0 ? 'translateY(0)' : 'translateY(24px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
                transitionDelay: '400ms',
              }}
            >
              {hero.subtext}
            </p>

          </div>

          <Footer />
        </section>

        {/* Section 2: Technology & Experience */}
        <section className="h-screen relative">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=1920&q=80')`,
            }}
          >
            <div className="absolute inset-0 bg-[#1a2a3a]/80" />
          </div>

          <div className="relative z-10 h-full flex items-center justify-center px-4 sm:px-6">
            <div className="max-w-6xl w-full grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
              {/* Technology */}
              <div
                className="text-center"
                style={{
                  opacity: currentSection === 1 ? 1 : 0,
                  transform: currentSection === 1 ? 'translateX(0)' : 'translateX(-30px)',
                  transition: 'opacity 0.6s ease, transform 0.6s ease',
                }}
              >
                <div className="mb-6 sm:mb-8">
                  <div className="w-px h-12 sm:h-16 bg-[#3eb8b3] mx-auto mb-4 sm:mb-6" />
                  <TrendingUp className="text-[#3eb8b3] mx-auto" size={40} strokeWidth={1} />
                </div>
                <h3 className="text-[#3eb8b3] text-xl sm:text-2xl tracking-widest mb-4 sm:mb-6">
                  {tech.title}
                </h3>
                <p className="text-white/80 text-base sm:text-lg leading-relaxed">
                  {tech.description}
                </p>
              </div>

              {/* Experience */}
              <div
                className="text-center"
                style={{
                  opacity: currentSection === 1 ? 1 : 0,
                  transform: currentSection === 1 ? 'translateX(0)' : 'translateX(30px)',
                  transition: 'opacity 0.6s ease, transform 0.6s ease',
                }}
              >
                <div className="mb-6 sm:mb-8">
                  <div className="w-px h-12 sm:h-16 bg-[#3eb8b3] mx-auto mb-4 sm:mb-6" />
                  <Users className="text-[#3eb8b3] mx-auto" size={40} strokeWidth={1} />
                </div>
                <h3 className="text-[#3eb8b3] text-xl sm:text-2xl tracking-widest mb-4 sm:mb-6">
                  {exp.title}
                </h3>
                <p className="text-white/80 text-base sm:text-lg leading-relaxed">
                  {exp.description}
                </p>
              </div>
            </div>
          </div>

          {/* Know Okuant button */}
          <div className="absolute bottom-20 sm:bottom-24 left-1/2 -translate-x-1/2 z-20">
            <Link
              to="/servicios"
              className="hover-glow text-[#3eb8b3] border border-[#3eb8b3] px-6 sm:px-8 py-3 text-xs sm:text-sm tracking-widest hover:bg-[#3eb8b3] hover:text-white transition-all duration-300"
            >
              {language === 'es' ? 'CONOCER OKUANT' : 'KNOW OKUANT'}
            </Link>
          </div>

          <Footer />
        </section>

        {/* Section 3: Services */}
        <section className="h-screen relative">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&q=80')`,
            }}
          >
            <div className="absolute inset-0 bg-[#1a2a3a]/75" />
          </div>

          <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 sm:px-6">
            <p
              className="text-[#3eb8b3] text-sm sm:text-lg mb-3 sm:mb-4 tracking-widest uppercase"
              style={{
                opacity: currentSection === 2 ? 1 : 0,
                transform: currentSection === 2 ? 'scale(1)' : 'scale(0.85)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
              }}
            >
              - {services.title} -
            </p>
            <h2
              className="text-white text-2xl sm:text-3xl md:text-4xl font-light mb-10 sm:mb-16 text-center max-w-3xl"
              style={{
                opacity: currentSection === 2 ? 1 : 0,
                transform: currentSection === 2 ? 'scale(1)' : 'scale(0.85)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
                transitionDelay: '100ms',
              }}
            >
              {services.subtitle}
            </h2>

            {/* Services grid */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-10 sm:gap-16 md:gap-32">
              {/* Investment */}
              <Link
                to="/servicios/inversion"
                className="group flex flex-col items-center"
                style={{
                  opacity: currentSection === 2 ? 1 : 0,
                  transform: currentSection === 2 ? 'scale(1)' : 'scale(0.85)',
                  transition: 'opacity 0.6s ease, transform 0.6s ease',
                  transitionDelay: '200ms',
                }}
              >
                <div className="relative w-32 h-32 sm:w-40 sm:h-40 mb-4 sm:mb-6">
                  <div className="absolute inset-0 rounded-full border border-white/30 group-hover:border-[#3eb8b3] transition-colors" />
                  <div className="absolute inset-3 sm:inset-4 rounded-full border border-dashed border-white/20 group-hover:border-[#3eb8b3]/50 transition-colors animate-spin-slow" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <TrendingUp className="text-white group-hover:text-[#3eb8b3] transition-colors" size={36} strokeWidth={1} />
                  </div>
                </div>
                <span className="text-white text-lg sm:text-xl tracking-wider group-hover:text-[#3eb8b3] transition-colors">
                  {services.investment.title}
                </span>
              </Link>

              {/* Management */}
              <Link
                to="/servicios/gestion"
                className="group flex flex-col items-center"
                style={{
                  opacity: currentSection === 2 ? 1 : 0,
                  transform: currentSection === 2 ? 'scale(1)' : 'scale(0.85)',
                  transition: 'opacity 0.6s ease, transform 0.6s ease',
                  transitionDelay: '350ms',
                }}
              >
                <div className="relative w-32 h-32 sm:w-40 sm:h-40 mb-4 sm:mb-6">
                  <div className="absolute inset-0 rounded-full border border-white/30 group-hover:border-[#3eb8b3] transition-colors" />
                  <div className="absolute inset-3 sm:inset-4 rounded-full border border-dashed border-white/20 group-hover:border-[#3eb8b3]/50 transition-colors animate-spin-slow" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Users className="text-white group-hover:text-[#3eb8b3] transition-colors" size={36} strokeWidth={1} />
                  </div>
                </div>
                <span className="text-white text-lg sm:text-xl tracking-wider group-hover:text-[#3eb8b3] transition-colors">
                  {services.management.title}
                </span>
              </Link>
            </div>
          </div>

          <Footer />
        </section>
      </div>

    </div>
  );
};

export default Home;
